import { cj } from 'src/utils/classNames';
import { InvariantChecker } from 'src/utils/UIInvariants/InvariantChecker';
import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	getTriggerPrice,
	MAX_LEVERAGE_ORDER_SIZE,
	OrderType,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { useCallback, useMemo, useEffect } from 'react';
import { useState } from 'react';
import Button from '../primitives/Button/Button';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import {
	SaveTpSlOrdersCallback,
	BracketOrders,
} from '../TradeForm/TpSlOrderForm/types';
import { useTpSlOrdersByMarket } from 'src/hooks/useTpSlOrdersByMarket';
import {
	UIMarket,
	UIOrderType,
	matchEnum,
	roundToStepSizeIfLargeEnough,
} from '@velocity-exchange/common';
import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useTpSlOrderFormReducer } from 'src/hooks/useTpSlOrderFormReducer';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useModal from 'src/components/Modals/hooks/useModal';
import TpSlOrderFormV2 from '../TradeForm/TpSlOrderForm/TpSlOrderFormV2';
import useDriftClient from 'src/hooks/useDriftClient';
import useOraclePrice from 'src/hooks/useOraclePrice';
import Divider from 'src/components/primitives/Divider/Divider';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { TabOption } from 'src/components/primitives/Tabs/types';
import { TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME } from 'src/components/TradeConfirmationModal/consts';
import ValueDisplay from 'src/components/ValueDisplay';
import { useOpenTpSlPerpOrders } from 'src/hooks/trade/perp/useOpenTpSlPerpOrders';

const TABS: TabOption<'entire' | 'partial'>[] = [
	{
		label: 'Entire Position',
		value: 'entire',
	},
	{
		label: 'Partial',
		value: 'partial',
	},
];

const TpSlOrderFormModalContent = (props: TpSlOrderFormModalProps) => {
	const userAccount = useAccountData();
	const openPerpPositions = useCurrentAccountPositions();
	const isMobile = useIsMobileScreenSize();
	const position = openPerpPositions.find(
		(position) => position.marketIndex === props.marketIndex
	);

	const uiMarket = UIMarket.createPerpMarket(position?.marketIndex ?? 0);
	const stepSize = useMarketStepSize(uiMarket.marketId);
	const markPrice = useMarkPrice(uiMarket.marketId);

	const [isSubmitting, setIsSubmitting] = useState(false);

	// Calculate maxBaseSizeString from position
	const openPositionSizeBigNum = BigNum.from(
		position?.baseSize.abs(),
		BASE_PRECISION_EXP
	);
	const maxBaseSizeString = roundToStepSizeIfLargeEnough(
		openPositionSizeBigNum.abs().toNum().toString(),
		stepSize
	);

	const [formReducerState, dispatchFormReducer] = useTpSlOrderFormReducer(
		maxBaseSizeString,
		stepSize,
		markPrice
	);

	const orders = useTpSlOrdersByMarket(
		position?.marketIndex,
		position?.direction === 'long'
	);

	// Order type state for TP/SL forms - separate for each
	// Initialize order types based on existing orders
	const [takeProfitOrderType, setTakeProfitOrderType] = useState<UIOrderType>(
		() => {
			if (
				!formReducerState.partialPositionSelected &&
				orders.takeProfitPositionOrder
			) {
				const orderType = orders.takeProfitPositionOrder.orderType;
				return matchEnum(orderType, OrderType.TRIGGER_LIMIT)
					? 'limit'
					: 'market';
			}
			return 'market';
		}
	);
	const [stopLossOrderType, setStopLossOrderType] = useState<UIOrderType>(
		() => {
			if (
				!formReducerState.partialPositionSelected &&
				orders.stopLossPositionOrder
			) {
				const orderType = orders.stopLossPositionOrder.orderType;
				return matchEnum(orderType, OrderType.TRIGGER_LIMIT)
					? 'limit'
					: 'market';
			}
			return 'market';
		}
	);

	// Initialize form with existing order values - only when on the "Entire Position" tab
	useEffect(() => {
		// Only prepopulate existing orders when not in partial mode (i.e., "Entire Position" tab is selected)
		if (
			!formReducerState.partialPositionSelected &&
			(orders.takeProfitPositionOrder || orders.stopLossPositionOrder)
		) {
			const takeProfitPrice = orders.takeProfitPositionOrder?.triggerPrice;
			const stopLossPrice = orders.stopLossPositionOrder?.triggerPrice;

			// Set order types based on existing orders
			if (orders.takeProfitPositionOrder) {
				const tpOrderType = orders.takeProfitPositionOrder.orderType;
				if (matchEnum(tpOrderType, OrderType.TRIGGER_LIMIT)) {
					setTakeProfitOrderType('limit');
				} else {
					setTakeProfitOrderType('market');
				}
			}

			if (orders.stopLossPositionOrder) {
				const slOrderType = orders.stopLossPositionOrder.orderType;
				if (matchEnum(slOrderType, OrderType.TRIGGER_LIMIT)) {
					setStopLossOrderType('limit');
				} else {
					setStopLossOrderType('market');
				}
			}

			dispatchFormReducer({
				type: 'setBracketOrders',
				value: {
					bracketOrders: {
						takeProfit: takeProfitPrice
							? {
									direction: position?.direction === 'long' ? 'short' : 'long',
									price: takeProfitPrice,
									orderType:
										orders.takeProfitPositionOrder &&
										matchEnum(
											orders.takeProfitPositionOrder.orderType,
											OrderType.TRIGGER_LIMIT
										)
											? 'limit'
											: 'market',
									limitPrice:
										orders.takeProfitPositionOrder &&
										matchEnum(
											orders.takeProfitPositionOrder.orderType,
											OrderType.TRIGGER_LIMIT
										)
											? orders.takeProfitPositionOrder.price
											: null,
								}
							: undefined,
						stopLoss: stopLossPrice
							? {
									direction: position?.direction === 'long' ? 'short' : 'long',
									price: stopLossPrice,
									orderType:
										orders.stopLossPositionOrder &&
										matchEnum(
											orders.stopLossPositionOrder.orderType,
											OrderType.TRIGGER_LIMIT
										)
											? 'limit'
											: 'market',
									limitPrice:
										orders.stopLossPositionOrder &&
										matchEnum(
											orders.stopLossPositionOrder.orderType,
											OrderType.TRIGGER_LIMIT
										)
											? orders.stopLossPositionOrder.price
											: null,
								}
							: undefined,
						anyInvalid: false,
					},
				},
			});
		} else if (formReducerState.partialPositionSelected) {
			// Clear existing order values when switching to partial mode
			dispatchFormReducer({
				type: 'setBracketOrders',
				value: {
					bracketOrders: {
						takeProfit: undefined,
						stopLoss: undefined,
						anyInvalid: false,
					},
				},
			});
		}
	}, [
		orders.takeProfitPositionOrder?.triggerPrice,
		orders.takeProfitPositionOrder?.price,
		orders.takeProfitPositionOrder?.orderType,
		orders.stopLossPositionOrder?.triggerPrice,
		orders.stopLossPositionOrder?.price,
		orders.stopLossPositionOrder?.orderType,
		position?.direction,
		dispatchFormReducer,
		formReducerState.partialPositionSelected,
		setTakeProfitOrderType,
		setStopLossOrderType,
	]);

	const stopLossTriggerPrice =
		formReducerState.bracketOrders.stopLoss?.price?.val ?? undefined;

	const stopLossPastLiqPrice =
		stopLossTriggerPrice &&
		!position?.liqPrice.eqn(-1) && // liquidation price = -1 means no liquidation price
		(position?.direction === 'long'
			? stopLossTriggerPrice.lt(position?.liqPrice)
			: stopLossTriggerPrice.gt(position?.liqPrice ?? new BN(0)));

	const { stopLoss, takeProfit, anyInvalid } = formReducerState.bracketOrders;
	// Allow submission if there are form values OR existing orders to edit

	const hasFormValues = !!stopLoss || !!takeProfit;
	const hasExistingOrders =
		!!orders.takeProfitPositionOrder || !!orders.stopLossPositionOrder;

	// Check if user has either:
	// 1. Existing order with form values that don't match it, OR
	// 2. No existing order but has form values
	const userHasValidInput = (() => {
		if (!hasFormValues) return false;

		if (!hasExistingOrders) {
			// No existing orders but has form values - valid input
			return true;
		}

		// Has existing orders - check if form values differ from existing orders
		const takeProfitChanged =
			takeProfit &&
			orders.takeProfitPositionOrder &&
			(!takeProfit.price?.eq(
				orders.takeProfitPositionOrder.triggerPrice ?? new BigNum(0)
			) ||
				takeProfit.orderType !== orders.takeProfitPositionOrder.orderType ||
				(takeProfit.limitPrice &&
					!takeProfit.limitPrice.eq(
						orders.takeProfitPositionOrder.price ?? new BigNum(0)
					)));

		const stopLossChanged =
			stopLoss &&
			orders.stopLossPositionOrder &&
			(!stopLoss.price?.eq(
				orders.stopLossPositionOrder.triggerPrice ?? new BigNum(0)
			) ||
				stopLoss.orderType !== orders.stopLossPositionOrder.orderType ||
				(stopLoss.limitPrice &&
					!stopLoss.limitPrice.eq(
						orders.stopLossPositionOrder.price ?? new BigNum(0)
					)));

		// Valid if form has new orders or existing orders have changed
		const hasNewTakeProfit = takeProfit && !orders.takeProfitPositionOrder;
		const hasNewStopLoss = stopLoss && !orders.stopLossPositionOrder;

		return (
			hasNewTakeProfit || hasNewStopLoss || takeProfitChanged || stopLossChanged
		);
	})();

	const buttonDisabled = anyInvalid || !userHasValidInput;

	const driftClient = useDriftClient();
	const oraclePrice = useOraclePrice(uiMarket.marketId);

	// Calculate trigger price using SDK function
	const referencePrice = useMemo(() => {
		if (!driftClient || !oraclePrice || position?.marketIndex === undefined) {
			return ZERO_PRICE_BIGNUM;
		}

		try {
			const marketAccount = driftClient.getPerpMarketAccount(
				position?.marketIndex
			);

			if (!marketAccount) {
				return ZERO_PRICE_BIGNUM;
			}

			const now = new BN(Math.floor(Date.now() / 1000)); // Current timestamp in seconds (integer)
			const referencePriceBN = getTriggerPrice(
				marketAccount,
				oraclePrice.val,
				now,
				true
			);

			return new BigNum(referencePriceBN, PRICE_PRECISION_EXP);
		} catch (error) {
			console.warn('Error calculating trigger price:', error);

			return ZERO_PRICE_BIGNUM;
		}
	}, [driftClient, oraclePrice, position?.marketIndex]);

	const openTpSlPerpOrders = useOpenTpSlPerpOrders();

	const saveOrdersCallback: SaveTpSlOrdersCallback = useCallback(
		(args) => {
			if (!args) return;
			dispatchFormReducer({
				type: 'setBracketOrders',
				value: { bracketOrders: args as BracketOrders },
			});
		},
		[dispatchFormReducer]
	);

	const handleTabChange = useCallback(
		(tab: 'entire' | 'partial') => {
			dispatchFormReducer({
				type: 'setPartialPositionSelected',
				value: { partialPositionSelected: tab === 'partial' },
			});
		},
		[dispatchFormReducer]
	);

	const handleFormSubmission = useCallback(
		async (close: () => void) => {
			setIsSubmitting(true);

			const existingOrderIdsToEdit: {
				takeProfit: number | undefined;
				stopLoss: number | undefined;
			} = { takeProfit: undefined, stopLoss: undefined };

			try {
				if (!formReducerState.partialPositionSelected) {
					// If not partial, check if the existing orders are different from the form state for editing purposes
					const takeProfitChanged =
						formReducerState.bracketOrders.takeProfit &&
						orders.takeProfitPositionOrder &&
						(!formReducerState.bracketOrders.takeProfit.price?.eq(
							orders.takeProfitPositionOrder.triggerPrice ?? new BigNum(0)
						) ||
							formReducerState.bracketOrders.takeProfit.orderType !==
								(matchEnum(
									orders.takeProfitPositionOrder.orderType,
									OrderType.TRIGGER_LIMIT
								)
									? 'limit'
									: 'market') ||
							(formReducerState.bracketOrders.takeProfit.limitPrice &&
								!formReducerState.bracketOrders.takeProfit.limitPrice.eq(
									orders.takeProfitPositionOrder.price ?? new BigNum(0)
								)));

					const stopLossChanged =
						formReducerState.bracketOrders.stopLoss &&
						orders.stopLossPositionOrder &&
						(!formReducerState.bracketOrders.stopLoss.price?.eq(
							orders.stopLossPositionOrder.triggerPrice ?? new BigNum(0)
						) ||
							formReducerState.bracketOrders.stopLoss.orderType !==
								(matchEnum(
									orders.stopLossPositionOrder.orderType,
									OrderType.TRIGGER_LIMIT
								)
									? 'limit'
									: 'market') ||
							(formReducerState.bracketOrders.stopLoss.limitPrice &&
								!formReducerState.bracketOrders.stopLoss.limitPrice.eq(
									orders.stopLossPositionOrder.price ?? new BigNum(0)
								)));

					if (takeProfitChanged) {
						existingOrderIdsToEdit.takeProfit =
							orders.takeProfitPositionOrder?.orderId;
					}
					if (stopLossChanged) {
						existingOrderIdsToEdit.stopLoss =
							orders.stopLossPositionOrder?.orderId;
					}
				}

				// Determine the base asset amount to use
				let baseAssetAmount: BigNum;

				if (!formReducerState.partialPositionSelected) {
					// If not partial, use MAX_LEVERAGE_ORDER_SIZE
					baseAssetAmount = new BigNum(MAX_LEVERAGE_ORDER_SIZE);
				} else {
					// If partial, check if we have individual sizes in bracket orders
					const hasIndividualTpSize =
						formReducerState.bracketOrders.takeProfit?.size;
					const hasIndividualSlSize =
						formReducerState.bracketOrders.stopLoss?.size;

					if (hasIndividualTpSize || hasIndividualSlSize) {
						// Use the individual size if available, prioritizing the one that exists
						baseAssetAmount =
							hasIndividualTpSize ??
							hasIndividualSlSize ??
							formReducerState.userInputEntrySize;
					} else {
						// Fallback to modal's reducer state
						baseAssetAmount = formReducerState.userInputEntrySize;
					}
				}
				const toastId = `tp-sl-orders-${Date.now()}`;
				await openTpSlPerpOrders(toastId, {
					bracketOrders: formReducerState.bracketOrders,
					targetMarketIndex: position?.marketIndex,
					baseAssetAmount,
					existingOrderIdsToEdit: existingOrderIdsToEdit,
				});

				// Force refresh account data to ensure position table updates
				if (userAccount?.client) {
					try {
						await userAccount.client.fetchAccounts();
					} catch (error) {
						console.warn(
							'Failed to refresh account data after TP/SL order update:',
							error
						);
					}
				}

				close();
			} catch (error) {
				// do nothing, error will be caught by the TP/SL hook and notification shown
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			formReducerState.partialPositionSelected,
			formReducerState.bracketOrders,
			formReducerState.userInputEntrySize,
			orders.takeProfitPositionOrder?.triggerPrice,
			orders.takeProfitPositionOrder?.orderId,
			orders.stopLossPositionOrder?.triggerPrice,
			orders.stopLossPositionOrder?.orderId,
			position?.marketIndex,
			openTpSlPerpOrders,
		]
	);

	if (!position) {
		return null;
	}

	return (
		<>
			<ResponsiveModal.Title
				tabs={TABS}
				activeTab={
					formReducerState.partialPositionSelected ? 'partial' : 'entire'
				}
				onTabChange={handleTabChange}
				closeButtonTestId="tpsl-modal-close"
			>
				Take Profit/Stop Loss
			</ResponsiveModal.Title>

			<ResponsiveModal.Body
				{...InvariantChecker.getInvariantCheckerContextWrapperHtmlAttributes(
					'perp-close-position-popup'
				)}
			>
				<div className={TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME}>
					<ValueDisplay.Default
						label="Position"
						value={
							<div
								className={cj(
									'capitalize',
									position.direction.match(/buy|long/)
										? 'text-text-positive-green-button'
										: 'text-text-negative-red-button'
								)}
							>
								{roundToStepSizeIfLargeEnough(
									formReducerState.positionEntrySize.toNum().toString(),
									stepSize
								)}{' '}
								{position.marketSymbol} {position.direction}
							</div>
						}
					/>
					<ValueDisplay.Default
						label="Entry Price"
						value={
							<NumberDisplay
								value={BigNum.from(position.entryPrice, PRICE_PRECISION_EXP)}
								precision="trade"
								type="currency"
							/>
						}
					/>
					<ValueDisplay.Default
						label={
							<DefinitionTooltip
								definitionId="tradingReferencePrice"
								side={!isMobile ? 'left' : undefined}
							>
								Reference Price
							</DefinitionTooltip>
						}
						value={
							<NumberDisplay
								value={referencePrice}
								precision="trade"
								type="currency"
							/>
						}
					/>
				</div>
				<TpSlOrderFormV2
					key="tp-sl-order-form-v2"
					direction={position.direction}
					entryPrice={new BigNum(position.entryPrice, PRICE_PRECISION_EXP)}
					entrySize={formReducerState.userInputEntrySize}
					initialEntrySize={formReducerState.positionEntrySize}
					saveOrdersCallback={saveOrdersCallback}
					savedTakeProfitPrice={
						formReducerState.partialPositionSelected
							? undefined
							: orders.takeProfitPositionOrder?.triggerPrice
					}
					savedStopLossPrice={
						formReducerState.partialPositionSelected
							? undefined
							: orders.stopLossPositionOrder?.triggerPrice
					}
					savedTakeProfitLimitPrice={
						formReducerState.partialPositionSelected
							? undefined
							: orders.takeProfitPositionOrder &&
								  matchEnum(
										orders.takeProfitPositionOrder.orderType,
										OrderType.TRIGGER_LIMIT
								  )
								? orders.takeProfitPositionOrder.price
								: undefined
					}
					savedStopLossLimitPrice={
						formReducerState.partialPositionSelected
							? undefined
							: orders.stopLossPositionOrder &&
								  matchEnum(
										orders.stopLossPositionOrder.orderType,
										OrderType.TRIGGER_LIMIT
								  )
								? orders.stopLossPositionOrder.price
								: undefined
					}
					takeProfitOrderId={orders.takeProfitPositionOrder?.orderId}
					stopLossOrderId={orders.stopLossPositionOrder?.orderId}
					takeProfitOrderType={takeProfitOrderType}
					stopLossOrderType={stopLossOrderType}
					setTakeProfitOrderType={setTakeProfitOrderType}
					setStopLossOrderType={setStopLossOrderType}
					usePercentBasisDropdown
					marketIndex={props.marketIndex}
					isStopLossBeyondLiquidationPriceProp={stopLossPastLiqPrice}
					allowTrailingStopLoss
					// Size-related props for individual TP/SL control
					partialPositionSelected={formReducerState.partialPositionSelected}
					stepSize={stepSize}
					baseAssetSymbol={uiMarket.uiSymbols.baseAssetDisplaySymbol}
					markPrice={markPrice}
				/>

				<Divider />

				<span>
					{formReducerState.partialPositionSelected
						? 'This setting allows you to set multiple TP/SL orders, each covering a different portion of your position'
						: 'This setting applies to the entire position, regardless of whether you add or reduce the position from here onwards. You can only set one position TP and one position SL.'}
				</span>
			</ResponsiveModal.Body>
			<ResponsiveModal.Footer>
				{({ close }) => (
					<Button
						size="lg"
						className="w-full"
						onClick={() => handleFormSubmission(close)}
						disabled={buttonDisabled}
						isLoading={isSubmitting}
						dataTestId="tpsl-modal-submit"
					>
						Confirm
					</Button>
				)}
			</ResponsiveModal.Footer>
		</>
	);
};

export interface TpSlOrderFormModalProps {
	marketIndex: number;
}

const TpSlOrderFormModal = ({ marketIndex }: TpSlOrderFormModalProps) => {
	const { hide } = useModal();

	return (
		<ResponsiveModal.Container onClose={hide} dataTestId="tpsl-modal">
			<TpSlOrderFormModalContent marketIndex={marketIndex} />
		</ResponsiveModal.Container>
	);
};

export default TpSlOrderFormModal;
